import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { GraduationCap } from 'lucide-react';
import { apiClient, useAuth } from '@/lib/api';
import { toast } from 'sonner';
import logger from '@/lib/logger';

const fmtAED = (v) => new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED', minimumFractionDigits: 0 }).format(v || 0);

/**
 * Drill-down modal for enrolled leads.
 * Filterable by period / team / agent. Used by both the Sales Dashboard's
 * Enrolled stat card AND the QuickStatsWidget's "Enrolled MTD" card.
 */
const EnrolledLeadsModal = ({ open, onOpenChange, defaultPeriod = 'month', scope }) => {
    const { user } = useAuth();
    const isManager = ['super_admin', 'admin', 'sales_manager'].includes(user?.role);
    const isTeamLeader = user?.role === 'team_leader';
    // Show agent filter for TLs (scoped to their team) + all managers.
    const showAgentFilter = isManager || isTeamLeader;

    const [period, setPeriod] = useState(defaultPeriod);
    const [team, setTeam] = useState('all');
    const [agent, setAgent] = useState('all');
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ items: [], total: 0, total_revenue: 0, total_pages: 0 });
    const [teams, setTeams] = useState([]);
    const [agents, setAgents] = useState([]);

    // Reset paging when filters change
    useEffect(() => { setPage(1); }, [period, team, agent]);

    useEffect(() => {
        if (!open) return;
        let aborted = false;
        const run = async () => {
            try {
                setLoading(true);
                const p = new URLSearchParams();
                // Always send the period — backend treats missing period as
                // "month" so we must explicitly pass "all" for all-time view.
                if (period) p.set('period', period);
                if (team !== 'all') p.set('team_id', team);
                if (agent !== 'all') p.set('assigned_to', agent);
                p.set('page', String(page));
                if (scope === 'floor') p.set('scope', 'floor');
                p.set('page_size', '50');
                const res = await apiClient.get(`/dashboard/enrolled-leads?${p.toString()}`);
                if (!aborted) setData(res.data);
            } catch (e) {
                logger.error('Failed to load enrolled leads:', e);
                if (!aborted) toast.error('Failed to load enrolled leads');
            } finally {
                if (!aborted) setLoading(false);
            }
        };
        run();
        return () => { aborted = true; };
    }, [open, period, team, agent, page, scope]);

    useEffect(() => {
        if (!open || !showAgentFilter) return;
        if (teams.length && agents.length) return;
        (async () => {
            try {
                const [t, a] = await Promise.all([
                    apiClient.get('/teams?department=Sales').catch(() => ({ data: [] })),
                    apiClient.get('/users?roles=sales_executive,team_leader,sales_manager').catch(() => ({ data: [] })),
                ]);
                setTeams(t.data || []);
                let list = (a.data || []).filter(u => u.is_active);
                // Strict TLs only see their own team's members. SM /
                // admin / super_admin keep full visibility — they have
                // the Team filter dropdown to narrow themselves. Using
                // team_id (reliable) not team_name (often null).
                if (isTeamLeader && !isManager && user?.team_id) {
                    list = list.filter(u => u.team_id === user.team_id);
                }
                setAgents(list);
            } catch (e) {
                logger.error('Failed to load filters:', e);
            }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="enrolled-drilldown-modal">
                <DialogHeader>
                    <DialogTitle className="flex flex-wrap items-center gap-2">
                        <GraduationCap className="h-5 w-5 text-emerald-500" />
                        Enrolled Leads
                        <Badge variant="secondary" className="ml-2" data-testid="enrolled-drilldown-count">
                            {data.total} {data.total === 1 ? 'lead' : 'leads'}
                        </Badge>
                        <Badge variant="secondary" data-testid="enrolled-drilldown-revenue">
                            {fmtAED(data.total_revenue)}
                        </Badge>
                    </DialogTitle>
                </DialogHeader>

                <div className="flex flex-wrap items-center gap-2 px-1 pb-2 border-b">
                    <Select value={period} onValueChange={setPeriod}>
                        <SelectTrigger className="w-[150px] h-9 text-xs" data-testid="enrolled-period-trigger">
                            <SelectValue placeholder="Period" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="today">Today</SelectItem>
                            <SelectItem value="week">Last 7 days</SelectItem>
                            <SelectItem value="month">This month</SelectItem>
                            <SelectItem value="quarter">Last 90 days</SelectItem>
                            <SelectItem value="year">This year</SelectItem>
                            <SelectItem value="all">All time</SelectItem>
                        </SelectContent>
                    </Select>
                    {isManager && (
                        <Select value={team} onValueChange={(v) => { setTeam(v); setAgent('all'); }}>
                            <SelectTrigger className="w-[160px] h-9 text-xs" data-testid="enrolled-team-trigger">
                                <SelectValue placeholder="Team" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Teams</SelectItem>
                                {teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    )}
                    {showAgentFilter && (
                        <Select value={agent} onValueChange={setAgent}>
                            <SelectTrigger className="w-[200px] h-9 text-xs" data-testid="enrolled-agent-trigger">
                                <SelectValue placeholder="Agent" />
                            </SelectTrigger>
                            <SelectContent className="max-h-[420px]">
                                <SelectItem value="all">All Agents</SelectItem>
                                {(team !== 'all' ? agents.filter(a => a.team_id === team) : agents).map(a => (
                                    <SelectItem key={a.id} value={a.id}>{a.full_name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                </div>

                <div className="overflow-auto flex-1 -mx-1 mt-2">
                    {loading ? (
                        <div className="space-y-2 p-2">
                            {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-10 w-full" />)}
                        </div>
                    ) : data.items.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">No enrolled leads in the selected range.</div>
                    ) : (
                        <table className="min-w-full text-sm">
                            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground sticky top-0">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">Name</th>
                                    <th className="text-left px-3 py-2 font-medium">Phone</th>
                                    <th className="text-left px-3 py-2 font-medium">Agent</th>
                                    <th className="text-left px-3 py-2 font-medium">Enrolled</th>
                                    <th className="text-right px-3 py-2 font-medium">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.items.map((ld, idx) => (
                                    <tr key={ld.id} className={idx % 2 ? 'bg-muted/20' : ''} data-testid={`enrolled-row-${ld.id}`}>
                                        <td className="px-3 py-2">{ld.full_name || '—'}</td>
                                        <td className="px-3 py-2 tabular-nums">{ld.phone || '—'}</td>
                                        <td className="px-3 py-2">{ld.assigned_to_name || '—'}</td>
                                        <td className="px-3 py-2 tabular-nums">{ld.enrolled_at ? new Date(ld.enrolled_at).toLocaleDateString() : '—'}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{fmtAED(ld.enrollment_amount || ld.sale_amount || 0)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {data.total_pages > 1 && (
                    <div className="flex items-center justify-between border-t pt-2 px-1 text-xs">
                        <span className="text-muted-foreground">Page {data.page} of {data.total_pages}</span>
                        <div className="flex gap-2">
                            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Prev</Button>
                            <Button size="sm" variant="outline" disabled={page >= data.total_pages} onClick={() => setPage(p => Math.min(data.total_pages, p + 1))}>Next</Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default EnrolledLeadsModal;
